import { useState, useEffect } from 'react';
import { useAuth } from "../../../../contexts/AuthContext";
import { db } from "../../../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import getServerTimestamp from '../../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorDisplay from '../../../../components/ErrorDisplay';
import Backdrop from "@mui/material/Backdrop";
import * as theme from '../../../../theme';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import TruCard from '../../../../components/TruCard';
import AddableList from '../../../../components/AddableList';

export default function EditEmotionsSummary() {
  const params = useParams();
  const clientId = params.clientId;
  const scanId = params.scanId;
  const reportId = params.reportId;
  const isOneOff = !!reportId;  // Synchronous determination: if reportId present, it's one-off
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    primaryDisharmony: '',
    factorsConnected: [],
    bachFlowerBalance: [{ item: '', description: '' }],
    emotionalImbalances: [],
    emotionsAffecting: [],
    bodyStressors: [],
    wordsOfAffirmation: { words: [], description: '' },
    limitingBeliefs: [],
    soulFilters: '',
  });
  const [scan, setScan] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Collapsible sections state - first section starts expanded, rest collapsed to reduce scrolling
  const [expandedSections, setExpandedSections] = useState({
    primaryDisharmony: true,
    factorsConnected: false,
    bachFlowerBalance: false,
    emotionalImbalances: false,
    emotionsAffecting: false,
    bodyStressors: false,
    wordsOfAffirmation: false,
    limitingBeliefs: false,
    soulFilters: false,
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    const getData = async () => {
      // Validate params before proceeding
      if (isOneOff && !reportId) {
        setError('Missing report ID for one-off edit.');
        return;
      }
      if (!isOneOff && (!clientId || !scanId)) {
        setError('Missing client or scan ID for standard edit.');
        return;
      }

      try {
        let docRef;
        if (isOneOff) {
          docRef = doc(db, "oneOffReports", reportId);
        } else {
          docRef = doc(db, "clients", clientId, "scans", scanId);
        }
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const reportData = data.reportData?.emotions || {};
          if (typeof reportData !== 'object' || reportData === null) {
            setError("This scan uses an older format and cannot be edited here.");
            navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`);
            return;
          }
          // Handle bachFlowerBalance: if it's an object (older format), convert to array
          let bachFlowerBalance = reportData.bachFlowerBalance || [{ item: '', description: '' }];
          if (bachFlowerBalance && !Array.isArray(bachFlowerBalance) && typeof bachFlowerBalance === 'object') {
            bachFlowerBalance = [bachFlowerBalance];
          }
          setFormData({
            primaryDisharmony: reportData.primaryDisharmony || '',
            factorsConnected: reportData.factorsConnected || [],
            bachFlowerBalance,
            emotionalImbalances: reportData.emotionalImbalances || [],
            emotionsAffecting: reportData.emotionsAffecting || [],
            bodyStressors: reportData.bodyStressors || [],
            wordsOfAffirmation: reportData.wordsOfAffirmation || { words: [], description: '' },
            limitingBeliefs: reportData.limitingBeliefs || [],
            soulFilters: reportData.soulFilters || '',
          });
          setScan(data);
        } else {
          setError("Data not found.");
        }
      } catch (err) {
        setError("Failed to fetch: " + err.message);
      }
    };
    getData();
  }, [clientId, scanId, reportId, isOneOff, navigate]);  // Note: isOneOff is now stable, but included for safety

  const handleTextChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNestedTextChange = (parentField, childField) => (e) => {
    setFormData({
      ...formData,
      [parentField]: { ...formData[parentField], [childField]: e.target.value }
    });
  };

  const addItem = (field, value) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], value.trim()]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const updateItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addWord = (value) => {
    setFormData({
      ...formData,
      wordsOfAffirmation: {
        ...formData.wordsOfAffirmation,
        words: [...formData.wordsOfAffirmation.words, value.trim()]
      }
    });
  };

  const removeWord = (index) => {
    const updatedWords = formData.wordsOfAffirmation.words.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      wordsOfAffirmation: { ...formData.wordsOfAffirmation, words: updatedWords }
    });
  };

  const updateWord = (index, value) => {
    const updatedWords = [...formData.wordsOfAffirmation.words];
    updatedWords[index] = value;
    setFormData({
      ...formData,
      wordsOfAffirmation: { ...formData.wordsOfAffirmation, words: updatedWords }
    });
  };

  const addToNestedObjectArray = (field, defaultObj = { item: '', description: '' }) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], defaultObj]
    });
  };

  const removeFromNestedObjectArray = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [field]: updated
    });
  };

  const updateNestedObjectArray = (field, index, key, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = { ...updatedArray[index], [key]: value };
    setFormData({
      ...formData,
      [field]: updatedArray
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const timestamp = await getServerTimestamp();
      const updatedData = {
        primaryDisharmony: formData.primaryDisharmony,
        factorsConnected: formData.factorsConnected,
        bachFlowerBalance: formData.bachFlowerBalance,
        emotionalImbalances: formData.emotionalImbalances,
        emotionsAffecting: formData.emotionsAffecting,
        bodyStressors: formData.bodyStressors,
        wordsOfAffirmation: formData.wordsOfAffirmation,
        limitingBeliefs: formData.limitingBeliefs,
        soulFilters: formData.soulFilters,
      };

      let docRef;
      if (isOneOff) {
        docRef = doc(db, "oneOffReports", reportId);
      } else {
        docRef = doc(db, "clients", clientId, "scans", scanId);
      }
      await updateDoc(docRef, {
        "reportData.emotions": updatedData,
        dateModified: timestamp,
      });

      navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      setError("Failed to update emotions summary: " + err.message);
    }

    setLoading(false);
  };

  return (
    <div style={theme.Container.basicWide}>
      <div style={theme.Heading}>EDIT EMOTIONS REPORT</div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <TruCard 
            title={'Primary Emotional Disharmony'}
            collapsible={true}
            expanded={expandedSections.primaryDisharmony}
            onToggle={() => toggleSection('primaryDisharmony')}
          >
            <TextField
              fullWidth
              multiline
              rows={2}
              value={formData.primaryDisharmony}
              onChange={handleTextChange('primaryDisharmony')}
              disabled={loading}
            />
          </TruCard>

          <TruCard 
            title={'Factors Connected to Disharmony'}
            collapsible={true}
            expanded={expandedSections.factorsConnected}
            onToggle={() => toggleSection('factorsConnected')}
          >
            <AddableList
              label="Add Factor"
              items={formData.factorsConnected}
              onAdd={(value) => addItem('factorsConnected', value)}
              onRemove={(index) => removeItem('factorsConnected', index)}
              onUpdate={(index, value) => updateItem('factorsConnected', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Bach Flower Balance'}
            collapsible={true}
            expanded={expandedSections.bachFlowerBalance}
            onToggle={() => toggleSection('bachFlowerBalance')}
          >
            <Box sx={{ mb: 2 }}>
              {formData.bachFlowerBalance.map((balance, index) => (
                <Box key={index} sx={{ mb: 2, border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                  <div style={{ marginBottom: 16, fontWeight: 'bold' }}>Bach Flower</div>
                  <TextField
                    label={`Item ${index + 1}`}
                    fullWidth
                    value={balance.item}
                    onChange={(e) => updateNestedObjectArray('bachFlowerBalance', index, 'item', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2}}
                  />
                  <TextField
                    label={`Description ${index + 1}`}
                    fullWidth
                    multiline
                    rows={2}
                    value={balance.description}
                    onChange={(e) => updateNestedObjectArray('bachFlowerBalance', index, 'description', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => removeFromNestedObjectArray('bachFlowerBalance', index)}
                    disabled={loading}
                  >
                    Remove Bach Flower
                  </Button>
                </Box>
              ))}
              <Button
                variant="outlined"
                onClick={() => addToNestedObjectArray('bachFlowerBalance', { item: '', description: '' })}
                disabled={loading}
              >
                Add Bach Flower
              </Button>
            </Box>
          </TruCard>

          <TruCard 
            title={'Emotional Imbalances'}
            collapsible={true}
            expanded={expandedSections.emotionalImbalances}
            onToggle={() => toggleSection('emotionalImbalances')}
          >
            <AddableList
              label="Add Imbalance"
              items={formData.emotionalImbalances}
              onAdd={(value) => addItem('emotionalImbalances', value)}
              onRemove={(index) => removeItem('emotionalImbalances', index)}
              onUpdate={(index, value) => updateItem('emotionalImbalances', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Emotions Affecting'}
            collapsible={true}
            expanded={expandedSections.emotionsAffecting}
            onToggle={() => toggleSection('emotionsAffecting')}
          >
            <AddableList
              label="Add Emotion Affecting"
              items={formData.emotionsAffecting}
              onAdd={(value) => addItem('emotionsAffecting', value)}
              onRemove={(index) => removeItem('emotionsAffecting', index)}
              onUpdate={(index, value) => updateItem('emotionsAffecting', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Body Stressors'}
            collapsible={true}
            expanded={expandedSections.bodyStressors}
            onToggle={() => toggleSection('bodyStressors')}
          >
            <AddableList
              label="Add Stressor"
              items={formData.bodyStressors}
              onAdd={(value) => addItem('bodyStressors', value)}
              onRemove={(index) => removeItem('bodyStressors', index)}
              onUpdate={(index, value) => updateItem('bodyStressors', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Words of Affirmation'}
            collapsible={true}
            expanded={expandedSections.wordsOfAffirmation}
            onToggle={() => toggleSection('wordsOfAffirmation')}
          >
            <AddableList
              label="Add Word"
              items={formData.wordsOfAffirmation.words}
              onAdd={addWord}
              onRemove={removeWord}
              onUpdate={updateWord}
              allowEdit={true}
              disabled={loading}
              isObject={false}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.wordsOfAffirmation.description}
              onChange={handleNestedTextChange('wordsOfAffirmation', 'description')}
              disabled={loading}
              sx={{ mt: 2 }}
            />
          </TruCard>

          <TruCard 
            title={'Limiting Beliefs'}
            collapsible={true}
            expanded={expandedSections.limitingBeliefs}
            onToggle={() => toggleSection('limitingBeliefs')}
          >
            <AddableList
              label="Add Belief"
              items={formData.limitingBeliefs}
              onAdd={(value) => addItem('limitingBeliefs', value)}
              onRemove={(index) => removeItem('limitingBeliefs', index)}
              onUpdate={(index, value) => updateItem('limitingBeliefs', index, value)}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={true}
            />
          </TruCard>

          <TruCard 
            title={'Soul Filters'}
            collapsible={true}
            expanded={expandedSections.soulFilters}
            onToggle={() => toggleSection('soulFilters')}
          >
            <TextField
              fullWidth
              multiline
              rows={2}
              value={formData.soulFilters}
              onChange={handleTextChange('soulFilters')}
              disabled={loading}
            />
          </TruCard>

          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </div>
  );
}